import * as pdfjs from "pdfjs-dist";
import type { TextItem } from "pdfjs-dist/types/src/display/api";
import type { TemplateBlank } from "./api";

/**
 * หา "จุดว่าง" บนแบบฟอร์ม PDF (เส้นประ ……/...... หรือ ____ ที่ไว้ให้เขียน) จาก text layer
 * พร้อมข้อความทั้งเอกสารที่มีตัวบอก [bN] แทนแต่ละจุด — ส่งให้ API ถาม AI ว่าจุดไหนเป็นช่องอะไร
 *
 * ตำแหน่งมาจากตัวอักษรจริงในไฟล์ จึงแม่นกว่าให้ AI กะจากภาพ; ไฟล์สแกน (ไม่มี text layer)
 * หาอะไรไม่เจอเลย ผู้เรียกต้องบอกผู้ใช้ให้วางเอง
 *
 * พิกัดเป็น pt จากมุมซ้ายบนของหน้า แบบเดียวกับ port.FormTemplateField ฝั่ง API
 */

export interface BlankScan {
  text: string;
  blanks: TemplateBlank[];
}

// จุด/ขีดที่ติดกัน — Word ตัดเส้นประเป็นชิ้นเล็ก ๆ หลายชิ้น ("." ".." "…") ทุกชิ้นจึงเป็น
// ส่วนหนึ่งของจุดว่างก่อน แล้วค่อยรวมชิ้นที่ติดกัน และตัดทิ้งตอนท้ายถ้ารวมแล้วสั้นกว่า 3 ตัว
// (… นับเป็น 3) — ไม่งั้น "ນາງ:." หรือจุดท้ายประโยคกลายเป็นช่อง
const LEADER_RUN = /[.…_]+/g;
const MIN_LEADER = 3;
const LEFT_INSET = 2;
const leaderLength = (s: string) => [...s].reduce((n, ch) => n + (ch === "…" ? 3 : 1), 0);
// ของที่คั่นระหว่างจุดว่างสองจุดแล้วยังนับเป็นช่องเดียวกัน (ວັນທີ ...../...../.....)
const DATE_JOINER = /^[\s/\-]*$/;

type Token =
  | { kind: "text"; text: string }
  | { kind: "blank"; x0: number; x1: number; baseline: number; size: number; raw: string; len: number };

interface Placed {
  str: string;
  x: number;
  baseline: number;
  size: number;
  width: number;
  font: string;
}

let measureCtx: CanvasRenderingContext2D | null = null;

/**
 * ความกว้างของ `part` เป็นสัดส่วนของทั้ง `str` — วัดด้วยฟอนต์ที่ pdf.js โหลดไว้ตอนวาดหน้า
 * (ชื่อ family = fontName ของ item) ถ้าฟอนต์ไม่อยู่ก็ได้ฟอนต์สำรอง ซึ่งยังพอให้สัดส่วนใกล้เคียง
 * ผู้ดูแลขยับต่อได้ — ดีกว่าแบ่งตามจำนวนตัวอักษรที่พังกับสระ/วรรณยุกต์ลาวที่กว้าง 0
 */
function fraction(part: string, str: string, font: string): number {
  measureCtx ??= document.createElement("canvas").getContext("2d");
  if (!measureCtx || !str) return 0;
  measureCtx.font = `20px ${font}`;
  const total = measureCtx.measureText(str).width;
  return total > 0 ? measureCtx.measureText(part).width / total : 0;
}

function tokenize(item: Placed): Token[] {
  const out: Token[] = [];
  let last = 0;
  for (const m of item.str.matchAll(LEADER_RUN)) {
    const at = m.index;
    if (at > last) out.push({ kind: "text", text: item.str.slice(last, at) });
    const x0 = item.x + item.width * fraction(item.str.slice(0, at), item.str, item.font);
    const x1 = item.x + item.width * fraction(item.str.slice(0, at + m[0].length), item.str, item.font);
    out.push({ kind: "blank", x0, x1, baseline: item.baseline, size: item.size, raw: m[0], len: leaderLength(m[0]) });
    last = at + m[0].length;
  }
  if (last < item.str.length) out.push({ kind: "text", text: item.str.slice(last) });
  return out;
}

/** รวมจุดว่างที่คั่นกันด้วยแค่ / - หรือช่องว่าง (วันที่แบบ วว/ດດ/ປປປປ) เป็นช่องเดียว */
function mergeDateParts(tokens: Token[]): Token[] {
  const out: Token[] = [];
  for (const t of tokens) {
    const prev = out[out.length - 1];
    const prev2 = out[out.length - 2];
    if (t.kind === "blank" && prev?.kind === "blank") {
      prev.x1 = Math.max(prev.x1, t.x1);
      prev.raw += t.raw;
      prev.len += t.len;
      continue;
    }
    if (t.kind === "blank" && prev?.kind === "text" && prev2?.kind === "blank" && DATE_JOINER.test(prev.text)) {
      out.pop();
      prev2.x1 = Math.max(prev2.x1, t.x1);
      prev2.raw += prev.text + t.raw;
      prev2.len += t.len;
      continue;
    }
    out.push(t);
  }
  // รวมแล้วยังสั้น = จุดธรรมดาในประโยค คืนเป็นข้อความตามเดิม
  return out.map((t) => (t.kind === "blank" && t.len < MIN_LEADER ? { kind: "text", text: t.raw } : t));
}

export async function scanBlanks(pdf: pdfjs.PDFDocumentProxy): Promise<BlankScan> {
  const lines: string[] = [];
  const blanks: TemplateBlank[] = [];

  for (let pageNr = 1; pageNr <= pdf.numPages; pageNr++) {
    const page = await pdf.getPage(pageNr);
    const vp = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();

    const placed: Placed[] = [];
    for (const raw of content.items) {
      if (!("str" in raw)) continue;
      const item = raw as TextItem;
      if (!item.str.trim()) continue;
      // แปลงจากพิกัด PDF (ล่างซ้าย) เป็นพิกัดของหน้า (บนซ้าย) ที่ scale 1 = pt
      const t = pdfjs.Util.transform(vp.transform, item.transform);
      const size = Math.hypot(t[2], t[3]);
      const family = content.styles[item.fontName]?.fontFamily ?? "sans-serif";
      placed.push({
        str: item.str,
        x: t[4],
        baseline: t[5],
        size,
        width: item.width,
        font: `"${item.fontName}", ${family}`,
      });
    }

    // จัดเป็นบรรทัด: baseline ห่างกันไม่เกิน 40% ของขนาดตัวอักษร = บรรทัดเดียวกัน
    placed.sort((a, b) => a.baseline - b.baseline || a.x - b.x);
    const rows: Placed[][] = [];
    for (const p of placed) {
      const row = rows[rows.length - 1];
      const ref = row?.[0];
      if (row && ref && Math.abs(p.baseline - ref.baseline) <= Math.max(ref.size, p.size) * 0.4) row.push(p);
      else rows.push([p]);
    }

    lines.push(`--- page ${pageNr} ---`);
    for (const row of rows) {
      row.sort((a, b) => a.x - b.x);
      const tokens: Token[] = [];
      let prevEnd: number | null = null;
      for (const p of row) {
        if (prevEnd !== null && p.x - prevEnd > p.size * 0.25) tokens.push({ kind: "text", text: " " });
        tokens.push(...tokenize(p));
        prevEnd = p.x + p.width;
      }

      let line = "";
      for (const t of mergeDateParts(tokens)) {
        if (t.kind === "text") {
          line += t.text;
          continue;
        }
        const id = `b${blanks.length + 1}`;
        // กล่องคลุมเหนือเส้นประขึ้นไปเท่าความสูงบรรทัด — ค่าที่เติมจะนั่งบนเส้นพอดี
        const height = Math.max(12, t.size * 1.45);
        const y = Math.max(0, t.baseline - t.size * 1.15);
        // เว้นจากป้ายด้านหน้า (":") นิดหน่อย ค่าที่เติมจะไม่ชนตัวอักษรของแบบฟอร์ม
        const x = Math.max(0, t.x0 + LEFT_INSET);
        const width = Math.min(vp.width, t.x1) - x;
        if (width < 6 || y + height > vp.height) continue;
        blanks.push({ id, page: pageNr, x, y, width, height });
        line += `[${id}]`;
      }
      if (line.trim()) lines.push(line.trim());
    }
  }
  return { text: lines.join("\n"), blanks };
}
